import {
  Component,
  type ElementRef,
  Renderer2,
  computed,
  inject,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { WINDOW } from '@tvd/cdk/browser';
import { BranchStore } from '@tvd/data-access/branch';
import { Icon } from '@tvd/design-system/icon';
import { BottomBarBookmarkComponent } from './action/bottom-bar-bookmark';
import { BottomBarMenuComponent } from './action/bottom-bar-menu';
import { BottomBarSearchComponent } from './action/bottom-bar-search';
import { BottomBarService } from './bottom-bar.service';

import { VIEW_TRANSITION_DECLARATIONS } from '@ngspot/view-transition';
import { NotificationCenterComponent } from '@tvd/ui/notification-center';

type ActionType = 'search' | 'menu' | 'bookmark' | null;

@Component({
  selector: 'tvd-bottom-bar',
  imports: [
    BottomBarSearchComponent,
    BottomBarMenuComponent,
    BottomBarBookmarkComponent,
    RouterLinkActive,
    RouterLink,
    ...VIEW_TRANSITION_DECLARATIONS,
    Icon,
    NotificationCenterComponent,
  ],
  host: {
    class:
      'block fixed bottom-0 left-0 right-0 z-[9999] bottom-bar-view-transition',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <tvd-notification-center />
    <div class="relative" vtNameForRouting="bottom-bar">
      <!-- Action Panel Dialog -->
      <dialog
        #actionPanel
        class="min-w-full bg-transparent focus:outline-0 sticky top-auto overflow-y-auto backdrop:opacity-80 transition-all duration-150 ease-out opacity-0 translate-y-5"
        (click)="closeAction()"
        (close)="handleDialogClose()"
      >
        <div
          #dialogContent
          tabindex="-1"
          class="w-full bg-background-50 border-t border-card-border overflow-hidden focus:outline-none"
        >
          @if (activeAction() === 'search') {
            <bottom-bar-search (close)="closeAction()" />
          } @else if (activeAction() === 'menu') {
            <bottom-bar-menu (close)="closeAction()" />
          } @else {
            <bottom-bar-bookmark (close)="closeAction()" />
          }
        </div>
      </dialog>

      <!-- Bottom Bar Buttons -->
      <div
        #bottomBarAnchor
        class="flex flex-row justify-between w-full h-18 items-center bg-card border-t border-card-border"
        [class.flex-row-reverse]="leftHandMode()"
      >
        <!-- calendar -->
        <button
          type="button"
          class="bottom-bar-button"
          [routerLinkActive]="['highlight']"
          [routerLinkActiveOptions]="{ exact: true }"
          [routerLink]="[
            '/dashboard',
            selectedBranchOrgId(),
            selectedBranchId(),
          ]"
          (click)="closeAction()"
        >
          <tvd-icon icon="calendar_clock" />
        </button>

        @if (branchCount() > 1) {
          <!-- bookmark -->
          <button
            type="button"
            class="bottom-bar-button absolute"
            [class.translate-x-18]="leftHandMode()"
            [class.-translate-x-18]="!leftHandMode()"
            [class.left-0]="leftHandMode()"
            [class.right-0]="!leftHandMode()"
            [class.active]="this.activeAction() === 'bookmark'"
            (click)="toggleAction($event, 'bookmark')"
            data-tour="branch-switcher"
          >
            <tvd-icon icon="bookmark" />
          </button>
        }

        <!-- menu -->
        <button
          type="button"
          class="bottom-bar-button"
          [class.active]="this.activeAction() === 'menu'"
          (click)="toggleAction($event, 'menu')"
          data-tour="mobile-menu"
        >
          <tvd-icon icon="menu" />
        </button>
      </div>
      <div class="bottom-safe-space bg-card"></div>
    </div>
  `,
})
export class BottomBarComponent {
  readonly hasBackdrop = output<boolean>();
  readonly #renderer = inject(Renderer2);
  readonly #window = inject(WINDOW);
  readonly #bottomBarService = inject(BottomBarService);
  readonly #branchStore = inject(BranchStore);

  readonly branches = this.#branchStore.branches;
  readonly selectedBranchId = this.#bottomBarService.selectedBranchId;
  readonly selectedBranchOrgId = this.#bottomBarService.selectedBranchOrgId;

  readonly branchCount = computed(() => this.branches().length);

  readonly leftHandMode = this.#bottomBarService.leftHandMode;
  readonly activeAction = signal<ActionType>(null);

  readonly dialogRef =
    viewChild.required<ElementRef<HTMLDialogElement>>('actionPanel');
  readonly dialog = computed(() => this.dialogRef().nativeElement);

  readonly dialogContentRef =
    viewChild.required<ElementRef<HTMLDivElement>>('dialogContent');
  readonly dialogContent = computed(
    () => this.dialogContentRef().nativeElement,
  );

  readonly anchorRef =
    viewChild.required<ElementRef<HTMLDivElement>>('bottomBarAnchor');
  readonly anchor = computed(() => this.anchorRef().nativeElement);

  #positionDialog() {
    const anchorEl = this.anchor();
    const dialog = this.dialog();
    const window = this.#window;

    if (!anchorEl || typeof window === 'undefined') {
      return;
    }
    this.#renderer.setStyle(dialog, 'bottom', '72px');
  }

  toggleAction(event: Event, action: ActionType) {
    event.preventDefault();
    event.stopPropagation();
    if (this.activeAction() === action) {
      this.closeAction();
    } else {
      this.openAction(action);
    }
  }

  openAction(action: ActionType) {
    this.hasBackdrop.emit(true);
    this.activeAction.set(action);
    const dialog = this.dialog();
    const dialogContent = this.dialogContent();

    requestAnimationFrame(() => {
      this.#positionDialog();
      dialog.show();
      requestAnimationFrame(() => {
        this.#renderer.removeClass(dialog, 'opacity-0');
        this.#renderer.removeClass(dialog, 'translate-y-5');
        this.#renderer.addClass(dialog, 'opacity-100');
        this.#renderer.addClass(dialog, 'translate-y-0');
        // Focus the content wrapper to prevent auto-focus on first link
        dialogContent.focus();
      });
    });
  }

  closeAction() {
    const dialog = this.dialog();
    this.hasBackdrop.emit(false);
    this.#renderer.removeClass(dialog, 'opacity-100');
    this.#renderer.removeClass(dialog, 'translate-y-0');
    this.#renderer.addClass(dialog, 'opacity-0');
    this.#renderer.addClass(dialog, 'translate-y-5');
    dialog.close();
  }

  handleDialogClose() {
    this.activeAction.set(null);
  }
}
